import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Trash2, Download, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeleteAccountSection = ({ userEmail, onStatusChange }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/account/deletion-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStatus(await res.json());
    } catch { /* non-blocking */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (token) fetchStatus();
    else setLoading(false);
  }, []);

  const handleRequestDeletion = async () => {
    setError(null);
    if (confirmEmail.trim().toLowerCase() !== (userEmail || '').toLowerCase()) {
      setError('The email you typed does not match your account email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/account/request-deletion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirm_email: confirmEmail.trim(), reason: reason.trim() || null }),
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        setShowConfirm(false);
        setConfirmEmail('');
        setReason('');
        if (onStatusChange) onStatusChange(data);
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || 'Failed to schedule deletion. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelDeletion = async () => {
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/account/cancel-deletion`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        if (onStatusChange) onStatusChange(data);
      } else setError('Failed to cancel deletion. Please try again.');
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const handleDownloadData = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/account/data-export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `30dayskills-data-export-${(userEmail || 'me').replace('@', '-at-')}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { setError('Could not download your data. Please try again.'); }
    finally { setExporting(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch { return iso.slice(0, 10); }
  };

  if (loading) {
    return (
      <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="delete-account-section">
        <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
      </section>
    );
  }

  if (status?.pending_deletion) {
    return (
      <section className="bg-red-950/30 border border-red-900/60 rounded-xl p-6" data-testid="delete-account-section">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-red-200 mb-1">Account scheduled for deletion</h2>
            <p className="text-red-300/80 text-sm">
              Your account will be permanently deleted on{' '}
              <strong className="text-red-100">{formatDate(status.deletion_purge_at)}</strong>.
              All your reflections, Living CV evidence, and history will be removed and cannot be recovered.
            </p>
            <p className="text-red-300/60 text-xs mt-2">You can cancel any time before that date.</p>
          </div>
        </div>
        {error && <p className="text-red-400 text-sm mb-3" data-testid="deletion-error">{error}</p>}
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={handleCancelDeletion} disabled={submitting} data-testid="cancel-deletion-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-red-950 text-sm font-medium hover:bg-zinc-100 disabled:opacity-60">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            Cancel deletion
          </button>
          <button type="button" onClick={handleDownloadData} disabled={exporting} data-testid="download-data-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-700/60 text-red-200 text-sm font-medium hover:bg-red-900/30 disabled:opacity-60">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download my data
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="delete-account-section">
      <h2 className="text-base font-semibold text-white mb-1">Privacy &amp; data</h2>
      <p className="text-zinc-500 text-sm mb-5">
        Download a copy of everything we hold about you, or permanently delete your account.
      </p>
      <div className="flex flex-wrap gap-3 mb-6">
        <button type="button" onClick={handleDownloadData} disabled={exporting} data-testid="download-data-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-zinc-700 text-zinc-200 text-sm font-medium hover:bg-zinc-800 disabled:opacity-60">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download my data (JSON)
        </button>
      </div>
      <div className="border-t border-zinc-800 pt-5">
        <h3 className="text-sm font-semibold text-red-300 mb-1 flex items-center gap-2">
          <Trash2 className="w-4 h-4" /> Delete my account
        </h3>
        <p className="text-zinc-500 text-sm mb-4">
          You&apos;ll have a 30-day grace period to change your mind. After that, your data is permanently erased.
        </p>
        {!showConfirm ? (
          <button type="button" onClick={() => setShowConfirm(true)} data-testid="open-delete-account-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-700/60 text-red-300 text-sm font-medium hover:bg-red-950/30">
            <Trash2 className="w-4 h-4" /> Delete account…
          </button>
        ) : (
          <div className="border border-red-900/60 bg-red-950/20 rounded-lg p-4 space-y-3">
            <p className="text-red-200 text-sm">
              Type <strong>{userEmail}</strong> to confirm. This starts a 30-day grace period.
            </p>
            <input type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder="Your account email" autoComplete="off" data-testid="confirm-email-input"
              className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-red-600" />
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Optional: why are you leaving? (helps us improve)" rows={2} maxLength={500}
              data-testid="deletion-reason-input"
              className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-red-600" />
            {error && <p className="text-red-400 text-sm" data-testid="deletion-error">{error}</p>}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handleRequestDeletion} disabled={submitting} data-testid="confirm-delete-account-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-500 disabled:opacity-60">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Schedule deletion
              </button>
              <button type="button" onClick={() => { setShowConfirm(false); setConfirmEmail(''); setReason(''); setError(null); }}
                disabled={submitting} data-testid="abort-delete-account-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-sm font-medium hover:bg-zinc-800 disabled:opacity-60">
                Keep my account
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DeleteAccountSection;
